import React from "react";
import { Button, CircularProgress } from "@material-ui/core";
import OpenInNew from "@material-ui/icons/OpenInNew";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import type { ModuleSettings } from "../../apis/user.apis";

interface ShopifyThemeEmbedPanelProps {
  embedStatus: ModuleSettings | null;
  checking: boolean;
  opening: boolean;
  onRefresh: () => void;
  onOpenThemeEditor: () => void;
  variant?: "modal" | "card";
}

export default function ShopifyThemeEmbedPanel({
  embedStatus,
  checking,
  opening,
  onRefresh,
  onOpenThemeEditor,
  variant = "modal",
}: ShopifyThemeEmbedPanelProps) {
  const { t } = useTranslation("shopify");
  const isEnabled =
    embedStatus?.theme_embed_enabled === true
    || embedStatus?.theme_embed_setup_complete === true;
  const footerClassName =
    variant === "modal"
      ? "theme-embed-required-dialog__footer d-flex flex-wrap align-items-center justify-content-end"
      : "shopify-theme-embed-panel__footer d-flex flex-wrap align-items-center justify-content-end";

  if (variant === "card" && isEnabled) {
    return (
      <div className="shopify-theme-embed-panel shopify-theme-embed-panel--enabled">
        <div className="shopify-theme-embed-panel__success-banner">
          <span>{t("integrations.themeEmbedEnabled")}</span>
          <Button
            type="button"
            variant="outlined"
            color="primary"
            size="small"
            disabled={!embedStatus?.theme_embed_url}
            onClick={onOpenThemeEditor}
          >
            {t("integrations.editEmbed")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={`shopify-theme-embed-panel shopify-theme-embed-panel--${variant}`}>
      {variant === "modal" && (
        <div className="theme-embed-required-dialog__info">
          <strong>{t("themeEmbedRequired.goodNewsTitle")}</strong>
          {" "}
          {t("themeEmbedRequired.goodNewsBody")}
        </div>
      )}

      {variant === "card" && !isEnabled && (
        <p className="shopify-theme-embed-panel__description">
          {t("themeEmbedRequired.subtitleBody")}
        </p>
      )}

      <p className="theme-embed-required-dialog__steps-heading">
        {t("themeEmbedRequired.howToActivate")}
      </p>

      <ol className="theme-embed-required-dialog__steps">
        <li className="theme-embed-required-dialog__step">
          <span className="theme-embed-required-dialog__step-title">
            {t("themeEmbedRequired.step1Title")}
          </span>
          <span className="theme-embed-required-dialog__step-body">
            {t("themeEmbedRequired.step1Body")}
          </span>
        </li>
        <li className="theme-embed-required-dialog__step">
          <span className="theme-embed-required-dialog__step-title">
            {t("themeEmbedRequired.step2Title")}
          </span>
          <span className="theme-embed-required-dialog__step-body">
            {t("themeEmbedRequired.step2Body")}
          </span>
        </li>
        <li className="theme-embed-required-dialog__step">
          <span className="theme-embed-required-dialog__step-title">
            {t("themeEmbedRequired.step3Title")}
          </span>
          <span className="theme-embed-required-dialog__step-body">
            {t("themeEmbedRequired.step3Body")}
          </span>
        </li>
      </ol>

      <p className="theme-embed-required-dialog__support">
        {t("themeEmbedRequired.supportLead")}{" "}
        <Link to="/support">{t("themeEmbedRequired.supportLink")}</Link>
        {" "}
        {t("themeEmbedRequired.supportTail")}
      </p>

      <div className={footerClassName}>
        <Button
          type="button"
          variant="outlined"
          color="primary"
          disabled={checking}
          onClick={onRefresh}
        >
          {checking && <CircularProgress size={16} className="me-2" />}
          {t("themeEmbedRequired.checkAgain")}
        </Button>
        <Button
          type="button"
          variant="contained"
          color="primary"
          className="positive-button"
          disabled={opening || !embedStatus?.theme_embed_url}
          onClick={onOpenThemeEditor}
          startIcon={!opening ? <OpenInNew /> : undefined}
        >
          {opening ? <CircularProgress size={16} color="inherit" /> : t("themeEmbedRequired.openThemeEditor")}
        </Button>
      </div>
    </div>
  );
}
